import { useEffect, useState } from "react"
import { Plus, Trash2 } from "lucide-react"
import type { AppShell } from "@/App"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Button } from "@/components/ui/button"
import {
  listContractedPowers,
  upsertContractedPower,
  deleteContractedPower,
  type ContractedPowerRow,
} from "@/lib/api"

interface DraftRow {
  name: string
  value_kva: string
  sort_order: string
  is_active: boolean
}

const EMPTY_DRAFT: DraftRow = {
  name: "",
  value_kva: "",
  sort_order: "0",
  is_active: true,
}

interface ContractedPowersTabProps {
  shell: AppShell | null
}

export function ContractedPowersTab({ shell }: ContractedPowersTabProps) {
  const [rows, setRows] = useState<ContractedPowerRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<DraftRow>(EMPTY_DRAFT)
  const [saving, setSaving] = useState(false)

  async function reload() {
    if (!shell) return
    setLoading(true)
    setError(null)
    try {
      const items = await listContractedPowers(shell)
      setRows(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha a carregar")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell])

  async function addRow() {
    if (!shell) return
    const kva = Number(draft.value_kva)
    if (!Number.isFinite(kva) || kva <= 0) {
      shell.bridge.toast({ variant: "error", description: "Valor kVA inválido" })
      return
    }
    setSaving(true)
    try {
      await upsertContractedPower(shell, {
        name: draft.name.trim() || `${kva} kVA`,
        value_kva: kva,
        is_active: draft.is_active,
        sort_order: Number.parseInt(draft.sort_order, 10) || 0,
      })
      setDraft(EMPTY_DRAFT)
      await reload()
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha a guardar",
      })
    } finally {
      setSaving(false)
    }
  }

  async function toggleActive(row: ContractedPowerRow) {
    if (!shell) return
    try {
      await upsertContractedPower(shell, {
        name: row.name,
        value_kva: Number(row.value_kva),
        is_active: !row.is_active,
        sort_order: row.sort_order,
      })
      await reload()
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha",
      })
    }
  }

  async function remove(row: ContractedPowerRow) {
    if (!shell) return
    try {
      await deleteContractedPower(shell, row.id)
      await reload()
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha a remover",
      })
    }
  }

  if (loading)
    return <p className="text-sm text-muted-foreground">A carregar…</p>
  if (error) return <p className="text-sm text-destructive">{error}</p>

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Lista de potências contratadas disponíveis no wizard. Valores em kVA.
      </p>

      <div className="overflow-hidden rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Designação</TableHead>
              <TableHead className="w-24">Valor kVA</TableHead>
              <TableHead className="w-20">Ordem</TableHead>
              <TableHead className="w-24">Activa</TableHead>
              <TableHead className="w-20 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{row.name}</TableCell>
                <TableCell>{Number(row.value_kva).toFixed(2)}</TableCell>
                <TableCell>{row.sort_order}</TableCell>
                <TableCell>
                  <Switch
                    checked={row.is_active}
                    onCheckedChange={() => toggleActive(row)}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => remove(row)}
                    title="Remover"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>
                <Input
                  placeholder="Designação (auto)"
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, name: e.target.value }))
                  }
                />
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="6.9"
                  value={draft.value_kva}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, value_kva: e.target.value }))
                  }
                />
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={draft.sort_order}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, sort_order: e.target.value }))
                  }
                />
              </TableCell>
              <TableCell>
                <Switch
                  checked={draft.is_active}
                  onCheckedChange={(v) =>
                    setDraft((d) => ({ ...d, is_active: v }))
                  }
                />
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="icon"
                  onClick={addRow}
                  disabled={saving}
                  title="Adicionar potência"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
